/**
 * The three answers a search has to record, as options a person can choose.
 *
 * **These mirror `InvestigationPurpose` and `LegalBasis` in the domain**, which the
 * OpenAPI document types as plain strings — the enums are enforced by the use case, not
 * by the schema, so there is nothing in `types/generated.ts` to import and the list has
 * to live here. The API rejects anything outside the enum with a 422 naming the valid
 * values, so a drift here is a bad select rather than a bad record.
 *
 * They are in `lib/` rather than beside one screen because two places now ask for the
 * same three answers: opening a case, and running a search that opens a research session
 * instead (ADR-0039). `features/cases/CasesPage.tsx` still carries its own copy and is
 * missing `public_task` and `vital_interests`; adopting these there is a one-line change
 * left out of this one deliberately, because it alters a form this correction was not
 * asked to touch.
 */

export interface AttestationOption {
  value: string;
  label: string;
}

/**
 * Why the search is being run. The four FCRA-regulated purposes are not members of the
 * domain enum at all, so they cannot appear here — a session cannot declare one for the
 * same reason a case cannot.
 */
export const PURPOSES: readonly AttestationOption[] = [
  { value: "due_diligence", label: "Due diligence" },
  { value: "kyc", label: "Know your customer" },
  { value: "fraud_investigation", label: "Fraud investigation" },
  { value: "cybersecurity", label: "Cybersecurity" },
  { value: "journalism", label: "Journalism" },
  { value: "brand_protection", label: "Brand protection" },
  { value: "self_footprint", label: "My own footprint" },
];

/**
 * The GDPR Art. 6 basis relied on. Listed in the enum's own order and **not ranked**:
 * `consent` is not stronger or weaker than `legitimate_interest`, it is a different basis
 * with different obligations (ADR-0040).
 */
export const LEGAL_BASES: readonly AttestationOption[] = [
  { value: "legitimate_interest", label: "Legitimate interest" },
  { value: "legal_obligation", label: "Legal obligation" },
  { value: "consent", label: "Consent" },
  { value: "contract", label: "Contract" },
  { value: "public_task", label: "Public task" },
  { value: "vital_interests", label: "Vital interests" },
];
